<script setup lang="ts">
import { callWithNuxt } from 'nuxt/app';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import SelectedCards from './_components/card-section/sections/SelectedCardSection.vue';
import PriceCalendar from './_components/price-calendar/PriceCalendar.vue';
import TicketHelper from './_components/TicketHelper.vue';
import TripViewSection from './_components/view-sections/TripViewSection.vue';
import PriceActions from './_components/price-actions/PriceActions.vue';
import FlightNoResult from './_components/FlightNoResult.vue';
import { useViewSectionsHandler } from '@composable/_local/hotel&view/useViewSectionsHandler';
import { useFilters } from '@composable/_local/hotel&view/useFilters';
import { useInit } from '@composable/_local/hotel&view/useInit';
import FiltersBox from '@components/ui/hotel&view/sort&filter/FiltersBox.vue';
import SortBox from '@components/ui/hotel&view/sort&filter/SortBox.vue';
import ViewSeoContent from '@components/ui/hotel&view/ViewSeoContent.vue';
import ViewHeader from '@components/ui/hotel&view/ViewHeader.vue';
import ViewFaqs from '@components/ui/hotel&view/ViewFaqs.vue';
import LoadingModal from '@components/ui/hotel&view/LoadingModal.vue';
import MobileActionBar from '@components/ui/hotel&view/MobileActionBar.vue';
import RealTimeLoading from '@components/ui/hotel&view/RealTimeLoading.vue';
import LoadingError from '@components/ui/hotel&view/LoadingError.vue';
import EmptyList from '@components/ui/hotel&view/EmptyList.vue';
import ChangeSearch from '@components/ui/hotel&view/ChangeSearch.vue';
import { type FaqFaqItemInner } from '@clients/ContentApi';
import ContentAlert from '@components/ui/index&view&widget/ContentAlert.vue';
import ReserveError from '@components/util/ReserveError.vue';
import TopBanners from '@components/ui/index&view/TopBanners.vue';
import 'assets/scss/shepherd.scss';
import type TripModule from '@itinerary/trip/TripModule';
import {
    type ItineraryPathData,
    ReserveType,
    type VerifyPathError,
} from '@utils/itinerary';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';
import { notifyTrack } from '@composable/usePriceTrackModal';
import VpnNotice from '@components/ui/hotel&view/VpnNotice.vue';

//#region important to call this before everything else
const searchFormStore = useSearchFormStore();
const route = useRoute();
searchFormStore.setReserveTypeBaseOnPath(route.path);
//#endregion

//#region definitions
const authStore = useAuthStore();
const itineraryStore = useItineraryStore();
const { currentReserveInfo, noResults, loading, viewBreadcrumbItems } =
    storeToRefs(itineraryStore);
const currentSearchForm = useCurrentSearchForm();
const currentModule = useCurrentItineraryModule<TripModule>();
const {
    currentLoadingStatus,
    originName,
    destinationName,
    canonicalRegex,
    description,
} = moduleToRefs(currentModule.value);
const { loadPriceEnabled } = moduleToRefs(currentSearchForm.value);
const { reserveType, needClearPackageSets } = storeToRefs(searchFormStore);
const contentStore = useContentStore();
const { searchResultDescription, loadingBanners } = classToRefs(contentStore);
const flightForm = useFlightForm();
const { isCapacityTrackCampaign } = moduleToRefs(flightForm);
const layoutStore = useLayoutStore();
const { isExtraFeaturesEnabled } = classToRefs(layoutStore);
const router = useRouter();
const { featureFlags } = storeToRefs(usePostHog());
const { isAirplaneTicket, isMrbilit } = useWhiteLabel();
const { isWebApp } = useDevice();
const pathData = computed(
    () =>
        ({
            ...route.params,
            ...route.query,
        }) as ItineraryPathData
);
const siteConfigs = useSiteConfigs();
const changeSearchRef = ref<InstanceType<typeof ChangeSearch>>();
const calendarRef = ref<InstanceType<typeof PriceCalendar>>();
const { isMobile } = useSize();
const { start, stop, forceReset } = useItineraryPackageReload(() =>
    itineraryStore.loadAvailable()
);
const forceReload = () => {
    forceReset();
    itineraryStore.forceLoadAvailable();
};
const {
    openFilters,
    closeFilters,
    showFiltersInMobile,
    applyFilters,
    sortRef,
    filtersRef,
} = useFilters();
// const { isOnline } = useNetwork();
const isOnline = ref(true); // temporary fix
const showChangeSearch = ref(false);
const fixBoxEnabled = ref(true);
const showingSuggestions = ref(false);
const changeSearchFixed = ref(false);
const isShowLoadingModal = ref(layoutStore.ssrIsShowLoadingModal);
const loadingBanner = computed(() =>
    loadingBanners.value
        .filter(b => new RegExp(b.urlPattern || '').test(route.path))
        .filter(
            m =>
                !m.displaySize ||
                m.displaySize === 'both' ||
                (m.displaySize === 'mobile' ? isMobile.value : !isMobile.value)
        )
);
//#endregion

//#region computed
const showSupportRequest = computed(
    () =>
        itineraryStore.noResults &&
        reserveType.value === ReserveType.Flight &&
        isMrbilit.value
);

const faqs = computed<
    {
        title: string;
        items: Array<{
            title: string;
            value: string;
        }>;
    }[]
>(() => {
    const faqItems = contentStore.searchResultFaqs;
    if (faqItems.length) {
        return [
            {
                title: '',
                items: faqItems.map((faq: FaqFaqItemInner) => ({
                    title: faq.question || '',
                    value: faq.answer || '',
                })),
            },
        ];
    } else {
        return [];
    }
});

const faqTitle = computed(
    () =>
        `سؤالات متداول بلیط ${currentSearchForm.value.reserveInfo.title} ${originName.value} ${itineraryStore.targetPersianName}`
);

const messages = computed(() => {
    if (!siteConfigs.showBanners) {
        return [];
    }
    return contentStore.filteredMessages.filter(m =>
        new RegExp(m.urlPattern || '').test(route.path)
    );
});

const canonicalUrl = computed(
    () =>
        `https://mrbilit.com${route.path
            .replace(canonicalRegex.value, '$1')
            .replace(/\/$/, '')}`
);

const metaTagTitle = computed(() => {
    if (isAirplaneTicket.value) {
        return currentModule.value.metaTagTitle.replaceAll(
            'مِستربلیط',
            'ایرپلین تیکت'
        );
    }
    return currentModule.value.metaTagTitle;
});

const metaTagDescription = computed(() => {
    const des = searchResultDescription.value || description.value!;
    if (isAirplaneTicket.value) {
        return des.replaceAll('مِستربلیط', 'ایرپلین تیکت');
    }
    return des;
});

const showEmptyList = computed(
    () =>
        itineraryStore.isLoadedSuccess &&
        (itineraryStore.noFilteredResults || itineraryStore.noResults) &&
        !currentModule.value.hasAutoReserve
);

const showBannedMessage = computed(
    () =>
        reserveType.value === ReserveType.Flight &&
        !loading.value &&
        !!featureFlags.value?.['flight-banned-message']
);

const userMobile = computed(() => authStore.userInfo?.mobile || null);
const showLoading = computed(
    () => isShowLoadingModal.value && !!Object.keys(route.query).length
);
//#endregion

//#region methods
const closeLoadingModal = () => {
    isShowLoadingModal.value = false;
    layoutStore.ssrIsShowLoadingModal = false;
};
const openLoadingModal = () => {
    if (loadingBanner.value[0]) {
        isShowLoadingModal.value = true;
        if (process.server) {
            layoutStore.ssrIsShowLoadingModal = true;
        }
    }
};
const init = useInit(pathData, route.path);
const initPage = async () => {
    if (needClearPackageSets.value) {
        itineraryStore.clearPackageSets();
        needClearPackageSets.value = false;
    }
    await init();
    start();
};
const showSearch = () => {
    if (isMobile.value) {
        changeSearchRef.value?.show();
    }
};
const onscroll = () => {
    const footer = (document as any).getElementById('__footer');
    if (footer) {
        fixBoxEnabled.value =
            footer.getBoundingClientRect().y >= window.innerHeight;
    }
};
const submitFilters = () => {
    applyFilters();
    closeFilters();
};

const customAdjustment = (adjustment: () => void) => {
    nextTick(() => {
        setTimeout(adjustment, 170);
    });
};
const { onViewNonReservableList, onViewPromotedList, onViewReservableList } =
    useViewSectionsHandler();
//#endregion

//#region watches
watch(loading, val => {
    if (val) {
        openLoadingModal();
    } else {
        closeLoadingModal();
    }
});
watch(
    [currentLoadingStatus, userMobile] as const,
    ([status, mobile], [, oldMobile]) => {
        if (status === LoadingStatus.Error) {
            if (mobile !== oldMobile) {
                notifyTrack({
                    isAutomatic: true,
                });
            }
        }
    }
);
//#endregion

//#region page setup
onServerPrefetch(async () => {
    contentStore.searchResultContentsLoading = true;
    const app = useNuxtApp();
    if (!currentSearchForm.value.searchReady) {
        try {
            await searchFormStore.verifyViewPath(route.path, pathData.value);
            await searchFormStore.loadViewPath(route.path, pathData.value);
            await Promise.all([
                contentStore.getSearchResultContents(),
                contentStore.loadBanners(),
            ]);
        } catch (e) {
            const err = e as VerifyPathError;
            callWithNuxt(app!, async () => {
                if (err.needRedirect && err.status && err.newUrl) {
                    await navigateTo(
                        {
                            path: err.newUrl,
                            query: { ...route.query, ...err.queries },
                        },
                        { redirectCode: err.status, replace: err.replace }
                    );
                } else if (
                    err.status !== undefined &&
                    err.message !== undefined
                ) {
                    showError({
                        statusCode: err.status,
                        message: err.message,
                    });
                } else {
                    grayLog()?.log(`${e}`, JSON.stringify(e), {
                        path: route.fullPath,
                    });
                    reportException(e);
                    showError({ statusCode: 500 });
                }
            });
        }
    }
    openLoadingModal();
});
useAsyncData(
    async () => {
        try {
            if (route.hash) {
                router.replace(route.fullPath.replace(/#.*/, ''));
            }
            await initPage();
            contentStore.loadViewPageContents();
            contentStore.getSearchResultContents();
        } catch (e) {
            reportException(e);
        }
        return true;
    },
    { lazy: true, server: false }
);
await useAsyncData('data', nuxtApp => {
    delete nuxtApp?.payload?.path;
    return Promise.resolve(true);
});
useHead({
    htmlAttrs: {
        class: 'hide-header-in-mobile',
    },
    link: [
        {
            rel: 'canonical',
            href: canonicalUrl,
        },
    ],
});
useSeoMeta({
    title: metaTagTitle,
    robots: () =>
        Object.keys(route.query).length ? 'noindex, follow' : 'index, follow',
    ogTitle: metaTagTitle,
    description: metaTagDescription,
    ogDescription: metaTagDescription,
    ogUrl: canonicalUrl,
});
definePageMeta({
    validate: ({ path }) => path !== '/view',
});
//#endregion

//#region lifecycle
onBeforeRouteLeave((to, from, next) => {
    if (to.path === from.path) {
        next();
        return;
    }
    const [, first, second] = to.fullPath.split('/');
    if (
        first !== 'reserve' &&
        (!['flights', 'trains', 'buses', 'taxi'].includes(first) || !second)
    ) {
        itineraryStore.currentSetIndex = 0;
    }
    next();
});
onMounted(() => {
    useLayoutStore().removeMobileNav();
    window.addEventListener('scroll', onscroll);
});
onBeforeUnmount(() => {
    stop();
    window.removeEventListener('scroll', onscroll);
});
//#endregion
</script>

<template>
    <div class="view-page-wrapper">
        <ClientOnly>
            <TopBanners />
        </ClientOnly>
        <Transition>
            <LoadingModal
                v-show="loadingBanner[0] && showLoading"
                :banner="loadingBanner[0]"
                :reserve-type="currentReserveInfo?.title || ''"
                :origin="originName"
                :destination="destinationName"
            />
        </Transition>
        <MobileActionBar
            fixed
            @open-filters="openFilters"
            @next-day="calendarRef?.goTomorrow()"
            @prev-day="calendarRef?.goYesterday()"
        />
        <ChangeSearch
            ref="changeSearchRef"
            @change-suggestions="showingSuggestions = $event"
            @change-fixed="changeSearchFixed = $event"
            @force-reload="forceReload"
        />
        <div class="view-panel-container">
            <mr-fix
                :offset-top="showingSuggestions ? 128 : 76"
                class="view-sidebar"
                stop-element="#view-stop-element"
                :class="{
                    'view-sidebar-mobile-visible': showChangeSearch,
                    'show-filters-in-mobile': showFiltersInMobile,
                }"
                element="aside"
                :disabled="isMobile"
            >
                <template #default="{ adjustment }">
                    <div class="fix-content">
                        <ViewHeader @show-search="showSearch" />
                        <LazyClientOnly>
                            <PriceActions desktop-only />
                        </LazyClientOnly>

                        <div class="filters-header mobile">
                            <mr-button
                                size="big"
                                variant="transparent"
                                :icon-button="{
                                    icon: faArrowRight,
                                    color: 'white',
                                }"
                                @click="closeFilters"
                            />
                            <div class="filters-title">فیلتر و مرتب‌سازی</div>
                            <mr-button
                                :icon-button="faArrowRight"
                                size="big"
                                class="hidden"
                            />
                        </div>
                        <div class="filters-container">
                            <SortBox
                                v-show="!noResults"
                                ref="sortRef"
                                :auto-apply="!isMobile"
                            />
                            <FiltersBox
                                ref="filtersRef"
                                :auto-apply="!isMobile"
                                @toggle="customAdjustment(adjustment)"
                            />
                        </div>
                        <div class="btn-accept">
                            <mr-button full-width @click="submitFilters">
                                تایید
                            </mr-button>
                        </div>
                    </div>
                </template>
            </mr-fix>
            <div class="view-body">
                <PriceCalendar
                    v-show="loadPriceEnabled && !isCapacityTrackCampaign"
                    ref="calendarRef"
                />
                <ContentAlert
                    v-for="message in messages"
                    :key="message.id"
                    class="view-message"
                    :message
                />
                <ClientOnly>
                    <SelectedCards />
                </ClientOnly>
                <TicketHelper />

                <ReserveError
                    v-if="!isOnline"
                    wrapper-id="connection-lost-offline"
                    error="خطایی در ارتباط رخ داد. لطفاً از اتصال خود به اینترنت مطمئن شوید و یا در صورت استفاده از فیلترشکن آن را خاموش و مجدداً تلاش کنید."
                    @research="router.replace('/')"
                />
                <template v-else>
                    <Transition>
                        <EmptyList v-show="showBannedMessage" disable-actions>
                            <template #message>
                                در حال حاضر به دلیل محدودیت‌های اعمال‌شده از سوی
                                سازمان هواپیمایی کشوری قادر به نمایش پروازها و
                                فروش بلیت هواپیما نیستیم. در حال پیگیری مستمر
                                برای رفع سریع‌تر مشکل هستیم. از صبوری و حمایت
                                شما سپاسگزاریم.
                            </template>
                        </EmptyList>
                    </Transition>
                    <ClientOnly>
                        <RealTimeLoading desktop-only margin="8px 0 8px 0" />
                    </ClientOnly>
                    <TripViewSection
                        @view-promoted-list="onViewPromotedList"
                        @view-reservable-list="onViewReservableList"
                        @view-non-reservable-list="onViewNonReservableList"
                    />
                    <Transition>
                        <EmptyList v-show="showEmptyList">
                            <template v-if="showSupportRequest" #no-result>
                                <FlightNoResult />
                            </template>
                        </EmptyList>
                    </Transition>
                    <Transition>
                        <LoadingError v-if="currentLoadingStatus === 'error'" />
                    </Transition>
                </template>
                <VpnNotice />
            </div>
        </div>
        <div id="view-stop-element" style="margin-top: -4px" />
        <template v-if="isExtraFeaturesEnabled">
            <ViewFaqs :title="faqTitle" :items="faqs" />
            <ViewSeoContent v-if="!isWebApp" />
        </template>
        <mr-breadcrumb class="crumb" :items="viewBreadcrumbItems" />
    </div>
</template>

<style scoped lang="scss">
.crumb {
    max-width: 1200px;
    width: 100%;
    margin-inline: auto;
    background-color: usePalette($p-cool-gray, $p-100);
    margin-top: 32px;
}
@include v-transition-opacity(0.3s);
.mobile {
    display: none;
    @include mobile-only {
        display: flex;
    }
}
.desktop {
    display: flex;
    @include mobile-only {
        display: none !important;
    }
}

.view-page-wrapper {
    position: relative;
}

.breadcrumb {
    margin-bottom: 4px;
    margin-top: 8px;
    @include mobile-only {
        margin-block: -4px;
    }
}

.view-panel-container {
    display: flex;
    width: 100%;
    max-width: $app-container-max-width;
    margin: 0 auto;

    .view-sidebar {
        position: relative;
        flex-basis: 25%;
        flex-grow: 25;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        width: 25%;
        min-width: 220px;
        margin: 0 0 0 12px;
        min-height: 200px;
        .fix-content {
            display: flex;
            flex-direction: column;
            @include desktop-only {
                gap: 8px;
                padding-top: 8px;
            }
        }
        .filters-container {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
    }

    .view-body {
        position: relative;
        flex-basis: 75%;
        flex-grow: 75;
        width: 70%;
        .view-message {
            margin-top: 8px;
        }
    }
}

.cards-container {
    margin-top: 4px;
}

.mobile-only {
    display: none;
}

.btn-accept,
.filters-header {
    display: none;
}

@media (max-width: $app-container-max-width) {
    .view-panel-container {
        padding: 0 16px;
    }
}

@media (max-width: $app-mobile-max-width) {
    .view-panel-container {
        position: relative;
        flex-direction: column;
        padding: 0;

        .view-sidebar {
            flex: 0;
            width: 100%;
            padding: 0;
            min-height: auto;
            min-width: auto;
            .filters-container {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: usePalette($p-cool-gray, $p-100);
                z-index: 1000100;
                overflow-y: auto;
                padding: 84px 12px 80px;
                transition: 200ms all;
                transform: translateY(100vh);
            }
            &.show-filters-in-mobile {
                .filters-header {
                    height: 72px;
                    width: 100%;
                    position: fixed;
                    top: 0;
                    right: 0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px;
                    background-color: usePalette();
                    z-index: 10000010;
                    .filters-title {
                        @include tg-display4($w7, usePalette($p-gray, $p-900));
                        margin: 0;
                        color: white;
                    }
                    .hidden {
                        visibility: hidden;
                    }
                }
                .filters-container {
                    transform: translateY(0);
                }

                .btn-accept {
                    display: flex;
                    width: 100%;
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background-color: white;
                    padding: 8px;
                    z-index: 10000010;
                    transform: translateZ(0);
                    > * {
                        margin-left: 8px;
                        margin-right: 8px;
                        &:first-child {
                            margin-right: 0;
                        }
                        &:last-child {
                            margin-left: 0;
                        }
                    }
                }
            }
        }

        .view-body {
            flex-grow: 1;
            width: 100%;
            padding: 8px 8px 66px 8px;
        }
    }

    .view-sidebar-mobile-visible {
        display: block;
    }

    .mobile-only {
        display: block;
    }
}
.fake-preload-image {
    display: none !important;
}
</style>
